import * as Yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum TagwallCustomerOrderInformationItemKeys {
  customerOrderNumber = 'customerOrderNumber',
  numberOfPkgs = 'numberOfPkgs',
  weight = 'weight',
  pallet = 'pallet',
  slip = 'slip',
  additionalShipperInfo = 'additionalShipperInfo',
}

export enum TagwallCarrierInformationItemKeys {
  handlingUnitQty = 'handlingUnitQty',
  handlingUnitType = 'handlingUnitType',
  packageQty = 'packageQty',
  packageType = 'packageType',
  weight = 'weight',
  hmx = 'hmx',
  commodityDescription = 'commodityDescription',
  nmfcNumber = 'nmfcNumber',
  class = 'class',
}

const TagwallCustomerOrderInformationItemSchema = Yup.object().shape({
  [TagwallCustomerOrderInformationItemKeys.customerOrderNumber]: Yup.string().nullable().optional(),
  [TagwallCustomerOrderInformationItemKeys.numberOfPkgs]: Yup.string().nullable().optional(),
  [TagwallCustomerOrderInformationItemKeys.weight]: Yup.string().nullable().optional(),
  [TagwallCustomerOrderInformationItemKeys.pallet]: Yup.string().nullable().optional(),
  [TagwallCustomerOrderInformationItemKeys.slip]: Yup.string().nullable().optional(),
  [TagwallCustomerOrderInformationItemKeys.additionalShipperInfo]: Yup.string()
    .nullable()
    .optional(),
})

const TagwallCustomerInformationSchema = Yup.object().shape({
  customerInformation: Yup.array().of(TagwallCustomerOrderInformationItemSchema).required(),
  shipperSignature: Yup.string().nullable().optional(),
})

const TagwallCarrierInformationItemSchema = Yup.object().shape({
  [TagwallCarrierInformationItemKeys.handlingUnitQty]: Yup.string().nullable().optional(),
  [TagwallCarrierInformationItemKeys.handlingUnitType]: Yup.string().nullable().optional(),
  [TagwallCarrierInformationItemKeys.packageQty]: Yup.string().nullable().optional(),
  [TagwallCarrierInformationItemKeys.packageType]: Yup.string().nullable().optional(),
  [TagwallCarrierInformationItemKeys.weight]: Yup.string().nullable().optional(),
  [TagwallCarrierInformationItemKeys.hmx]: Yup.string().nullable().optional(),
  [TagwallCarrierInformationItemKeys.commodityDescription]: Yup.string().nullable().optional(),
  [TagwallCarrierInformationItemKeys.nmfcNumber]: Yup.string().nullable().optional(),
  [TagwallCarrierInformationItemKeys.class]: Yup.string().nullable().optional(),
})

const TagwallCarrierInformationSchema = Yup.object().shape({
  carrierInformation: Yup.array().of(TagwallCarrierInformationItemSchema).required(),
  grandTotal: Yup.string().nullable().optional(),
  carrierSignature: Yup.string().nullable().optional(),
})

export const tagwallBillOfLadingFileDataSchema = Yup.object().shape({
  organizationLogoOverride: Yup.string().nullable().optional(),
  date: Yup.string().nullable().optional(),
  billOfLadingNumber: Yup.string().nullable().optional(),
  carrierName: Yup.string().nullable().optional(),
  trailerNumber: Yup.string().nullable().optional(),
  sealNumber: Yup.string().nullable().optional(),
  scac: Yup.string().nullable().optional(),
  proNumber: Yup.string().nullable().optional(),
  shipFrom: Yup.object().shape({
    name: Yup.string().nullable().optional(),
    address: Yup.string().nullable().optional(),
    cityStateZip: Yup.string().nullable().optional(),
    vendorNumber: Yup.string().nullable().optional(),
    sidNumber: Yup.string().nullable().optional(),
    fob: Yup.string().nullable().optional(),
  }),
  shipTo: Yup.object().shape({
    name: Yup.string().nullable().optional(),
    address: Yup.string().nullable().optional(),
    cityStateZip: Yup.string().nullable().optional(),
    telephone: Yup.string().nullable().optional(),
    cidNumber: Yup.string().nullable().optional(),
    fob: Yup.string().nullable().optional(),
  }),
  thirdPartyFrieghtChargesBillTo: Yup.object().shape({
    name: Yup.string().nullable().optional(),
    address: Yup.string().nullable().optional(),
    cityStateZip: Yup.string().nullable().optional(),
  }),
  freightChargeTerms: Yup.object().shape({
    prepaid: Yup.string().nullable().optional(),
    collect: Yup.string().nullable().optional(),
    thirdParty: Yup.string().nullable().optional(),
  }),
  masterBillOfLadingWithAttachedUnderlyingBillsOfLading: Yup.string().nullable().optional(),
  customerInformation: TagwallCustomerInformationSchema,
  carrierInformation: TagwallCarrierInformationSchema,
  codAmount: Yup.string().nullable().optional(),
  feeTerms: Yup.string().nullable().optional(),
  collect: Yup.string().nullable().optional(),
  prepaid: Yup.string().nullable().optional(),
  customerCheckAcceptable: Yup.string().nullable().optional(),
  carrierInstructions: Yup.string().nullable().optional(),
})

export type TagwallBillOfLadingFileData = Yup.InferType<typeof tagwallBillOfLadingFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<TagwallBillOfLadingFileData>

export const tagwallBillOfLadingFileMappings: RequiredFileData = {
  organizationLogoOverride: 'c6464d97-00e1-47e2-841a-0de4e07ceb14',
  date: '9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d',
  billOfLadingNumber: '1b9d6bcd-bbfd-4b2d-9b5d-ab8dfbbd4bed',
  carrierName: '6ec0bd7f-11c0-43da-975e-2a8ad9ebae0b',
  trailerNumber: '7c9e6679-7425-40de-944b-e07fc1f90ae7',
  sealNumber: '8f7d3c5e-1d92-4d93-8f71-0e9a8f2d6ab3',
  scac: '9a4b8c3d-2e81-4e94-9f62-1f8b9d3e7ac4',
  proNumber: 'a5c9d4e2-3f72-4f85-9d53-2c9c0e4f8bd5',
  shipFrom: {
    name: 'b6d0e5f3-4a63-4a96-8d44-3c0d1f5a9ce6',
    address: 'c7e1f6a4-5b54-4b07-9e35-4d1e2a6b0df7',
    cityStateZip: 'd8f2a7b5-6c45-4c18-8f26-5e2f3b7c1ea8',
    vendorNumber: 'e9a3b8c6-7d36-4d29-9a17-6f3a4c8d2fb9',
    sidNumber: 'f0b4c9d7-8e27-4e30-9b08-7a4b5d9e3ac0',
    fob: 'a1c5d0e8-9f18-4f41-9c99-8b5c6e0f4bd1',
  },
  shipTo: {
    name: 'b2d6e1f9-0a09-4a52-9d80-9c6d7f1a5ce2',
    address: 'c3e7f2a0-1b90-4b63-9e71-0d7e8a2b6df3',
    cityStateZip: 'd4f8a3b1-2c81-4c74-9f62-1e8f9b3c7ae4',
    telephone: 'e5a9b4c2-3d72-4d85-9a53-2f9a0c4d8be5',
    cidNumber: 'f6b0c5d3-4e63-4e96-9b44-3a0b1d5e9cf6',
    fob: 'a7c1d6e4-5f54-4f07-9c35-4b1c2e6f0da7',
  },
  thirdPartyFrieghtChargesBillTo: {
    name: 'b8d2e7f5-6a45-4a18-9d26-5c2d3e7f1eb8',
    address: 'c9e3f8a6-7b36-4b29-9e17-6d3e4f8a2fc9',
    cityStateZip: 'd0f4a9b7-8c27-4c30-9f08-7e4f5a9b3ad0',
  },
  freightChargeTerms: {
    prepaid: 'e1a5b0c8-9d18-4d41-9a99-8f5a6b0c4be1',
    collect: 'f2b6c1d9-0e09-4e52-9b80-9a6b7c1d5cf2',
    thirdParty: 'a3c7d2e0-1f90-4f63-9c71-0b7c8d2e6da3',
  },
  masterBillOfLadingWithAttachedUnderlyingBillsOfLading: 'b4d8e3f1-2a81-4a74-9d62-1c8d9e3f7ab4',
  customerInformation: {
    customerInformation: [
      {
        customerOrderNumber: 'c5e9f4a2-3b72-4b85-9e53-2d9e0f4a8bc5',
        numberOfPkgs: 'd6f0a5b3-4c63-4c96-9f44-3e0f1a5b9cd6',
        weight: 'e7a1b6c4-5d54-4d07-9a35-4f1a2b6c0de7',
        pallet: 'f8b2c7d5-6e45-4e18-9b26-5a2b3c7d1ef8',
        slip: 'a9c3d8e6-7f36-4f29-9c17-6b3c4d8e2fa9',
        additionalShipperInfo: 'b0d4e9f7-8a27-4a30-9d08-7c4d5e9f3ab0',
      },
    ],
    shipperSignature: 'c964a2c3-1df2-49a2-b3f5-7384bbdfb55b',
  },
  carrierInformation: {
    carrierInformation: [
      {
        handlingUnitQty: 'c1e5f0a8-9b18-4b41-9e99-8d5e6f0a4bc1',
        handlingUnitType: 'd2f6a1b9-0c09-4c52-9f80-9e6f7a1b5cd2',
        packageQty: 'e3a7b2c0-1d90-4d63-9a71-0f7a8b2c6de3',
        packageType: 'f4b8c3d1-2e81-4e74-9b62-1a8b9c3d7ef4',
        weight: 'a5c9d4e2-3f72-4f85-9c53-2b9c0d4e8af5',
        hmx: 'b6d0e5f3-4a63-4a96-9d44-3c0d1e5f9ba6',
        commodityDescription: 'c7e1f6a4-5b54-4b07-9e35-4d1e2f6a0cb7',
        nmfcNumber: 'd8f2a7b5-6c45-4c18-9f26-5e2f3a7b1dc8',
        class: 'e9a3b8c6-7d36-4d29-9a17-6f3a4b8c2ed9',
      },
    ],
    carrierSignature: 'be0bea9a-8055-4194-ad20-8a2eba906e67',
    grandTotal: 'f0b4c9d7-8e27-4e30-9b08-7a4b5c9d3fe0',
  },
  codAmount: 'a1c5d0e8-9f18-4f41-9c99-8b5c6d0e4aa1',
  feeTerms: 'b2d6e1f9-0a09-4a52-9d80-9c6d7e1f5bb2',
  collect: 'c3e7f2a0-1b90-4b63-9e71-0d7e8f2a6cc3',
  prepaid: 'd4f8a3b1-2c81-4c74-9f62-1e8f9a3b7dd4',
  customerCheckAcceptable: 'e5a9b4c2-3d72-4d85-9a53-2f9a0b4c8ee5',
  carrierInstructions: 'f6b0c5d3-4e63-4e96-9b44-3a0b1c5d9ff6',
}
